import React, { useEffect, useState } from 'react';
import { ResponsivePie } from '@nivo/pie';
import { BASE_URL } from '../../Components/Constant';
import ApiService from '../../Components/apiService.js';
import { Spin } from 'antd';

const MyPieChart = ({ reportId }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeId, setActiveId] = useState(null);


    const colorScheme = [
        "#8dd3c7", "#ffffb3", "#bebada", "#fb8072", "#80b1d3",
        "#fdb462", "#b3de69", "#fccde5", "#d9d9d9", "#bc80bd",
        "#ccebc5", "#ffed6f"
    ];

    useEffect(() => {
        const apiServiceForRecords = new ApiService(
            `${BASE_URL}/mt_reports/${reportId}`,
            { 'Content-Type': 'application/json' },
            'GET'
        );

        apiServiceForRecords
            .makeCall()
            .then(response => {
                if (response.report && response.data) {
                    const groupField = response.report.group_fields?.[0] || 'Unknown';
                    const aggregationField = response.report.aggregation_fields?.[0];

                    const groupedData = response.data.reduce((acc, record) => {
                        let groupValue = record[groupField];

                        // ✅ If group field is an object → use .Name
                        if (groupValue && typeof groupValue === "object") {
                            groupValue = groupValue.Name || "Unknown";
                        }

                        // fallback when null/undefined
                        if (!groupValue) {
                            groupValue = "Unknown";
                        }

                        let aggregateValue = 1;

                        if (aggregationField) {
                            let rawAggValue = record[aggregationField];

                            // ✅ If aggregation field is an object → use numeric value or Name
                            if (rawAggValue && typeof rawAggValue === "object") {
                                rawAggValue = rawAggValue.Name || 0;
                            }

                            aggregateValue = parseFloat(rawAggValue) || 0;
                        }

                        acc[groupValue] = (acc[groupValue] || 0) + aggregateValue;
                        return acc;
                    }, {});


                    const pieChartData = Object.keys(groupedData).map(groupValue => ({
                        id: groupValue,
                        label: groupValue,
                        value: groupedData[groupValue]
                    }));

                    setData(pieChartData);
                }
                setLoading(false);
            })
            .catch(err => {
                setError(err);
                setLoading(false);
            });
    }, [reportId]);

    if (!reportId) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'orange', fontWeight: 'bold' }}>
                Report not attached correctly.
            </div>
        );
    }

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'red', fontWeight: 'bold' }}>
                Error: {error.message || error.error}
            </div>
        );
    }

    return (
        <ResponsivePie
            data={data}
            margin={{ top: 20, right: 120, bottom: 60, left: 60 }}
            innerRadius={0.5}
            padAngle={0.7}
            cornerRadius={3}
            colors={({ id }) => {
                const index = data.findIndex(d => d.id === id);
                const baseColor = colorScheme[index % colorScheme.length];

                if (!activeId) return baseColor;
                return activeId === id ? baseColor : "#d9d9d9";
            }}

            onClick={(d) => {
                setActiveId(prev => prev === d.id ? null : d.id);
            }}

            borderWidth={1}
            borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsTextColor="#333333"
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: "color" }}
            arcLabelsSkipAngle={10}
            arcLabelsTextColor={{ from: "color", modifiers: [["darker", 2]] }}
            tooltip={({ datum }) => {
                const safeValue = typeof datum.value === "number" ? datum.value : 0;
                const total = data.reduce((sum, d) => sum + (d.value || 0), 0);
                return (
                    <div
                        style={{
                            padding: 8,
                            background: "white",
                            border: "1px solid #ccc",
                            borderRadius: 4,
                            fontSize: 12,
                        }}
                    >
                        <strong>{datum.label}</strong>
                        <br />
                        Value: {safeValue.toFixed(2)} <br />
                        Percentage: {((safeValue / total) * 100).toFixed(2)}%
                    </div>
                );
            }}
            legends={[
                {
                    anchor: "right",   // 👈 could be "bottom", "left", or "top"
                    direction: "column",
                    justify: false,
                    translateX: 100,   // push into right margin
                    translateY: 0,
                    itemsSpacing: 8,
                    itemWidth: 100,
                    itemHeight: 20,
                    itemTextColor: "#555",
                    itemDirection: "left-to-right",
                    symbolSize: 16,
                    symbolShape: "circle",
                    onClick: (legendItem) => {
                        const clicked = legendItem.id;
                        setActiveId(prev => (prev === clicked ? null : clicked));
                    },
                },
            ]}
        />
    );
};

export default MyPieChart;
